import React from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import {
  displayPrice,
  isPriceVariationsEnabled,
  requireListingImage,
} from '../../util/configHelpers';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { formatMoney } from '../../util/currency';
import { getListingSale } from '../../util/listingSale';
import { ensureListing, ensureUser } from '../../util/data';
import { computeLikesCount, isListingFavorited } from '../../util/favoritesHelpers';
import { richText } from '../../util/richText';
import { createResourceLocatorString } from '../../util/routes';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';
import { updateProfile } from '../../containers/ProfileSettingsPage/ProfileSettingsPage.duck';
import { types as sdkTypes } from '../../util/sdkLoader';
import * as listingVisibility from '../../util/listingVisibility';
import {
  selectFavoriteIds,
  selectIsFavoriteInProgress,
  toggleFavorite,
} from '../../ducks/favorites.duck';
import { addItemToCart, removeItemFromCart, selectIsInCart } from '../../ducks/cart.duck';
import { resolveMaxQuantityForListing } from '../../util/cartHelpers';
import { getListingSizeDisplay } from '../../util/sizeHelpers';

import {
  AvatarSmall,
  AspectRatioWrapper,
  ImageSlider,
  ListingCardThumbnail,
  NamedLink,
  ResponsiveImage,
} from '../../components';

import css from './ListingCard.module.css';
import IconBag from '../IconBag/IconBag';
import IconBagFilled from '../IconBagFilled/IconBagFilled';
import IconHeart from '../IconHeart/IconHeart';
import IconHeartFilled from '../IconHeartFilled/IconHeartFilled';
import {
  getListingStoreOrAuthorName,
  getProductImages,
  getRecentlyViewed,
  getUsername,
} from '../../util/dataExtractors';

const MIN_LENGTH_FOR_LONG_WORDS = 10;
const MAX_RECENTLY_VIEWED_ITEMS = 11;
const warnedMissingListingTypeForSearch = new Set();
const photoAsUrl = photo =>
  typeof photo === 'string' ? photo : typeof photo?.url === 'string' ? photo.url : null;

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const firstDefined = values => values.find(v => v !== null && v !== undefined && v !== '');
const { Money } = sdkTypes;
const { isListingPubliclyVisible } = listingVisibility;

const toMinorUnitAmount = rawAmount => {
  if (typeof rawAmount === 'number' && Number.isFinite(rawAmount)) {
    return Math.trunc(rawAmount);
  }
  if (typeof rawAmount === 'string' && /^-?\d+$/.test(rawAmount.trim())) {
    return Number(rawAmount);
  }
  if (!rawAmount || typeof rawAmount !== 'object') {
    return null;
  }
  if (typeof rawAmount?.value !== 'undefined') {
    const nestedValue = toMinorUnitAmount(rawAmount.value);
    if (nestedValue !== null) {
      return nestedValue;
    }
  }
  if (typeof rawAmount?.toString === 'function') {
    const asString = rawAmount.toString();
    if (/^-?\d+$/.test(`${asString}`.trim())) {
      return Number(asString);
    }
  }
  if (Number.isFinite(rawAmount.low) && Number.isFinite(rawAmount.high)) {
    const low = rawAmount.low >>> 0;
    const high = rawAmount.high | 0;
    return high * 4294967296 + low;
  }
  return null;
};

const asMoney = price => {
  if (!price) {
    return null;
  }
  if (price instanceof Money) {
    return price;
  }

  const transitMoneyValue = price['~#mn'] || price['#mn'];
  if (Array.isArray(transitMoneyValue) && transitMoneyValue.length === 2) {
    const [amountRaw, currencyRaw] = transitMoneyValue;
    const amount = toMinorUnitAmount(amountRaw);
    if (amount !== null && typeof currencyRaw === 'string') {
      return new Money(amount, currencyRaw);
    }
  }

  const amount = toMinorUnitAmount(price?.amount);
  const currency = price?.currency;
  if (amount !== null && typeof currency === 'string') {
    return new Money(amount, currency);
  }

  return null;
};

const optionLabel = value => {
  if (!value) {
    return null;
  }
  if (typeof value === 'string') {
    return value;
  }
  return value.label || value.name || null;
};

const getBrandLabel = publicData => {
  return optionLabel(
    firstDefined([
      publicData?.brandOption,
      publicData?.brand,
      publicData?.dancewear_brandOption,
      publicData?.dancewear_brand,
      publicData?.costume_brandOption,
      publicData?.costume_brand,
      publicData?.shoe_brandOption,
      publicData?.shoe_brand,
    ])
  );
};

const getConditionLabel = publicData => {
  return optionLabel(firstDefined([publicData?.conditionOption, publicData?.condition]));
};

const buildRecentlyViewedIds = (currentViewedIds, listingId) => {
  const existingIds = Array.isArray(currentViewedIds) ? currentViewedIds.filter(Boolean) : [];
  if (!listingId) {
    return existingIds.slice(0, MAX_RECENTLY_VIEWED_ITEMS);
  }

  return [listingId, ...existingIds.filter(id => id !== listingId)].slice(
    0,
    MAX_RECENTLY_VIEWED_ITEMS
  );
};

const areArraysEqual = (a, b) => {
  if (a.length !== b.length) {
    return false;
  }
  return a.every((value, index) => value === b[index]);
};

const priceData = (price, currency, intl) => {
  const normalizedPrice = asMoney(price);

  if (normalizedPrice && normalizedPrice.currency === currency) {
    const formattedPrice = formatMoney(intl, normalizedPrice);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (normalizedPrice) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: normalizedPrice.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: normalizedPrice.currency }
      ),
    };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const PriceMaybe = props => {
  const { price, publicData, config, intl, listingTypeConfig, sale } = props;
  const showPrice = displayPrice(listingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, listingTypeConfig);
  const hasMultiplePriceVariants = isPriceVariationsInUse && publicData?.priceVariants?.length > 1;

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);

  const priceValue = sale ? (
    <span className={css.salePrice}>
      <span className={css.originalPrice}>{formatMoney(intl, sale.originalPrice)}</span>
      <span className={classNames(css.priceValue, css.salePriceValue)}>{formattedPrice}</span>
      <span className={css.saleBadge}>
        <FormattedMessage
          id="ListingCard.salePercentOff"
          values={{ percentOff: sale.percentOff }}
        />
      </span>
    </span>
  ) : (
    <span className={css.priceValue}>{formattedPrice}</span>
  );
  const pricePerUnit = isBookable ? (
    <span className={css.perUnit}>
      <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
    </span>
  ) : (
    ''
  );

  return (
    <div className={css.price} title={priceTitle}>
      {hasMultiplePriceVariants ? (
        <FormattedMessage
          id="ListingCard.priceStartingFrom"
          values={{ priceValue, pricePerUnit }}
        />
      ) : (
        <FormattedMessage id="ListingCard.price" values={{ priceValue, pricePerUnit }} />
      )}
    </div>
  );
};

const ListingCardImage = props => {
  const {
    currentListing,
    setActivePropsMaybe,
    title,
    renderSizes,
    aspectWidth,
    aspectHeight,
    variantPrefix,
    showListingImage,
    style,
    imgAlt,
    singleImage = false,
  } = props;

  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;

  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants)
        .filter(k => k === 'custom' || k === 'scaled-large')
        .sort((a, b) => {
          if (a === 'custom') return -1;
          if (b === 'custom') return 1;
          return 0;
        })
    : [];

  const photosFromPublicData = Array.isArray(currentListing?.attributes?.publicData?.photos)
    ? currentListing.attributes.publicData.photos.map(photoAsUrl).filter(Boolean)
    : [];
  const photosFromSdk = getProductImages(currentListing.images);
  const imageUrls = Array.from(new Set([...photosFromPublicData, ...photosFromSdk]));
  const firstImageFallbackUrl =
    firstImage?.attributes?.variants?.['scaled-large']?.url ||
    firstImage?.attributes?.variants?.custom?.url ||
    firstImage?.attributes?.variants?.[`${variantPrefix}-2x`]?.url ||
    firstImage?.attributes?.variants?.['scaled-medium']?.url ||
    null;
  const sliderImages =
    imageUrls.length > 0 ? imageUrls : firstImageFallbackUrl ? [firstImageFallbackUrl] : [];
  const sharedAspectMediaClass = classNames(css.cardMedia, css.aspectRatioWrapper);

  if (singleImage && showListingImage && sliderImages.length > 0) {
    return (
      <div className={css.compactMedia} {...setActivePropsMaybe}>
        <img className={css.compactImage} src={sliderImages[0]} alt={imgAlt} loading="lazy" />
      </div>
    );
  }

  return showListingImage ? (
    sliderImages.length > 0 ? (
      <div className={classNames(css.cardMedia, css.sliderMediaFrame)} {...setActivePropsMaybe}>
        <div className={css.cardImg}>
          <ImageSlider images={sliderImages} isMobile={false} imgAlt={imgAlt} />
        </div>
      </div>
    ) : (
      <AspectRatioWrapper
        className={sharedAspectMediaClass}
        width={aspectWidth}
        height={aspectHeight}
        {...setActivePropsMaybe}
      >
        <LazyImage
          rootClassName={css.rootForImage}
          alt={title}
          image={firstImage}
          variants={variants}
          sizes={renderSizes}
        />
      </AspectRatioWrapper>
    )
  ) : (
    <ListingCardThumbnail
      style={style}
      listingTitle={title}
      className={sharedAspectMediaClass}
      width={aspectWidth}
      height={aspectHeight}
      setActivePropsMaybe={setActivePropsMaybe}
    />
  );
};

export const ListingCard = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = props.intl || useIntl();
  const dispatch = useDispatch();
  const history = useHistory();

  const {
    className,
    rootClassName,
    listing,
    renderSizes,
    setActiveListing,
    onListingClick,
    showAuthorInfo = true,
    isSearchResultCard = false,
    compact = false,
  } = props;

  const classes = classNames(rootClassName || css.root, className, {
    [css.searchResultCard]: isSearchResultCard,
  });
  const currentListing = ensureListing(listing);
  if (!isListingPubliclyVisible(currentListing)) {
    return null;
  }

  const author = ensureUser(currentListing.author);
  const id = currentListing.id.uuid;
  const { title = '', price, publicData = {} } = currentListing.attributes;
  const sale = getListingSale(currentListing);
  const slug = createSlug(title);
  const favoriteIds = useSelector(selectFavoriteIds);
  const isFavoriteUpdating = useSelector(state => selectIsFavoriteInProgress(state, id));
  const isInCart = useSelector(state => selectIsInCart(state, id));
  const currentUser = useSelector(state => state.user?.currentUser);
  const isAuthenticated = useSelector(state => state.auth?.isAuthenticated);
  const currentUserId = currentUser?.id?.uuid;
  const isOwnListing = !!(currentUserId && author?.id?.uuid === currentUserId);

  const { listingType, cardStyle } = publicData;
  const listingTypeConfig = config.listing.listingTypes.find(
    conf => conf.listingType === listingType
  );
  const hasAnyImageData =
    (Array.isArray(currentListing.images) && currentListing.images.length > 0) ||
    (Array.isArray(publicData?.photos) && publicData.photos.length > 0);
  const showListingImage = hasAnyImageData || requireListingImage(listingTypeConfig);

  if (
    process.env.NODE_ENV === 'development' &&
    isSearchResultCard &&
    !listingTypeConfig &&
    id &&
    !warnedMissingListingTypeForSearch.has(id)
  ) {
    // eslint-disable-next-line no-console
    console.warn('[ListingCard] Missing listingType config for search card listing', {
      listingId: id,
      listingType,
      hasAnyImageData,
    });
    warnedMissingListingTypeForSearch.add(id);
  }

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  const brand = getBrandLabel(publicData);
  const condition = getConditionLabel(publicData);
  const sizeDisplay = getListingSizeDisplay(publicData);
  const size = sizeDisplay?.isMultiple
    ? intl.formatMessage({
        id: 'SearchListingCard.multipleSizesAvailable',
        defaultMessage: 'Multiple sizes available',
      })
    : sizeDisplay?.label;
  const isFavorited = isListingFavorited(favoriteIds, id);
  const likesCount = computeLikesCount({ listing: currentListing, favoriteIds, currentUserId });
  const sellerUsername = getUsername(author);
  const sellerId = author?.id?.uuid;
  const sellerRoute = sellerUsername
    ? { name: 'StorePage', params: { username: sellerUsername } }
    : sellerId
    ? { name: 'ProfilePage', params: { id: sellerId } }
    : null;
  const sellerName =
    getListingStoreOrAuthorName(currentListing) ||
    intl.formatMessage({ id: 'SearchListingCard.sellerFallback' });
  const imgAlt = `${title} ${sellerName}`.trim();

  const stopCardNavigation = e => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onToggleFavorite = e => {
    stopCardNavigation(e);
    if (!isOwnListing && !isFavoriteUpdating) {
      dispatch(toggleFavorite(id));
    }
  };

  const onToggleCart = e => {
    stopCardNavigation(e);
    if (isInCart) {
      dispatch(removeItemFromCart({ listingId: id }));
    } else {
      dispatch(
        addItemToCart({
          listingId: id,
          quantity: 1,
          maxQuantity: resolveMaxQuantityForListing(currentListing),
          sellerId: author?.id?.uuid,
        })
      );
    }
  };

  const onSellerClick = e => {
    stopCardNavigation(e);
    if (!sellerRoute) {
      return;
    }

    history.push(
      createResourceLocatorString(sellerRoute.name, routeConfiguration, sellerRoute.params, {})
    );
  };

  const handleListingNavigation = () => {
    if (typeof onListingClick === 'function') {
      onListingClick({ listingId: id });
    }

    if (!isAuthenticated || !id) {
      return;
    }

    const existingRecentlyViewed = getRecentlyViewed(currentUser);
    const nextRecentlyViewed = buildRecentlyViewedIds(existingRecentlyViewed, id);
    if (areArraysEqual(existingRecentlyViewed, nextRecentlyViewed)) {
      return;
    }

    dispatch(
      updateProfile({
        publicData: {
          recentlyViewed: nextRecentlyViewed,
        },
      })
    );
  };

  const favoriteButton = isOwnListing ? null : (
    <button
      type="button"
      className={classNames(css.likesButton, {
        [css.likesButtonActive]: isFavorited,
        [css.compactLikesButton]: compact,
      })}
      onClick={onToggleFavorite}
      aria-label={intl.formatMessage({ id: 'SearchListingCard.favorite' })}
      aria-pressed={isFavorited}
    >
      {isFavorited ? <IconHeartFilled /> : <IconHeart />}
      {compact ? null : <span className={css.likesCount}>{likesCount}</span>}
    </button>
  );

  // Photo-first tile (homepage grids): one photo with a heart, then size, price and brand
  if (compact) {
    return (
      <NamedLink
        className={classNames(css.compactRoot, className)}
        name="ListingPage"
        params={{ id, slug }}
        onClick={handleListingNavigation}
        title={title}
      >
        <div className={css.compactMediaWrapper}>
          <ListingCardImage
            renderSizes={renderSizes}
            title={title}
            currentListing={currentListing}
            setActivePropsMaybe={setActivePropsMaybe}
            aspectWidth={3}
            aspectHeight={4}
            variantPrefix={variantPrefix}
            style={cardStyle}
            showListingImage={showListingImage}
            imgAlt={imgAlt}
            singleImage
          />
          {favoriteButton}
        </div>
        <div className={css.compactInfo}>
          {sizeDisplay?.isMultiple ? (
            <div className={css.compactSize}>
              <FormattedMessage id="ListingCard.compactMultipleSizes" />
            </div>
          ) : size ? (
            <div className={css.compactSize}>{size}</div>
          ) : null}
          <PriceMaybe
            price={price}
            publicData={publicData}
            config={config}
            intl={intl}
            listingTypeConfig={listingTypeConfig}
            sale={sale}
          />
          {brand ? <div className={css.compactBrand}>{brand}</div> : null}
        </div>
      </NamedLink>
    );
  }

  return (
    <NamedLink
      className={classes}
      name="ListingPage"
      params={{ id, slug }}
      onClick={handleListingNavigation}
    >
      <div className={css.sellerRow}>
        {showAuthorInfo ? (
          <button
            type="button"
            className={css.sellerInfoButton}
            onClick={onSellerClick}
            disabled={!sellerRoute}
          >
            <AvatarSmall user={author} disableProfileLink className={css.sellerAvatar} />
            <span className={css.sellerText}>
              <FormattedMessage
                id="SearchListingCard.soldBy"
                values={{ sellerName: <span className={css.sellerName}>{sellerName}</span> }}
              />
            </span>
          </button>
        ) : (
          <span className={css.sellerPlaceholder} />
        )}

        {!isOwnListing && (
          <button
            type="button"
            className={classNames(css.headerActionButton, isInCart ? css.added : null)}
            onClick={onToggleCart}
            aria-label={
              isInCart
                ? intl.formatMessage({
                    id: 'SearchListingCard.removeFromBag',
                    defaultMessage: 'Remove from cart',
                  })
                : intl.formatMessage({
                    id: 'SearchListingCard.addToBag',
                    defaultMessage: 'Add to cart',
                  })
            }
          >
            {isInCart ? <IconBagFilled /> : <IconBag />}
          </button>
        )}
      </div>

      <div className={css.imageWrapper}>
        <ListingCardImage
          renderSizes={renderSizes}
          title={title}
          currentListing={currentListing}
          setActivePropsMaybe={setActivePropsMaybe}
          aspectWidth={aspectWidth}
          aspectHeight={aspectHeight}
          variantPrefix={variantPrefix}
          style={cardStyle}
          showListingImage={showListingImage}
          imgAlt={imgAlt}
        />
        {sale ? (
          // On phones the price row is too narrow for the badge, so it sits on the photo instead
          <span className={classNames(css.saleBadge, css.photoSaleBadge)}>
            <FormattedMessage
              id="ListingCard.salePercentOff"
              values={{ percentOff: sale.percentOff }}
            />
          </span>
        ) : null}
      </div>

      <div className={css.info}>
        <div className={css.mainInfo}>
          <PriceMaybe
            price={price}
            publicData={publicData}
            config={config}
            intl={intl}
            listingTypeConfig={listingTypeConfig}
            sale={sale}
          />
          {favoriteButton}
        </div>

        <div className={css.title}>
          {richText(title, {
            longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
            longWordClass: css.longWord,
          })}
        </div>

        <div className={css.metaSection}>
          <div className={css.metaRow}>
            <span className={css.metaLabel}>
              <FormattedMessage id="SearchListingCard.brand" />:
            </span>
            <span>{brand || intl.formatMessage({ id: 'SearchListingCard.unknown' })}</span>
          </div>

          <div className={css.metaRow}>
            <span className={css.metaLabel}>
              <FormattedMessage id="SearchListingCard.condition" />:
            </span>
            <span>{condition || intl.formatMessage({ id: 'SearchListingCard.unknown' })}</span>
          </div>

          <div className={css.metaRow}>
            <span className={css.metaLabel}>
              <FormattedMessage id="SearchListingCard.size" />:
            </span>
            <span>{size || intl.formatMessage({ id: 'SearchListingCard.unknown' })}</span>
          </div>
        </div>
      </div>
    </NamedLink>
  );
};

export default ListingCard;
